import React, { useState, useMemo } from "react";

import { formatDate } from "../../services/date";

import InternalModal from "../../commons/InternalModal";

import Table from "./Table";
import KpiComposition from "./KpiComposition";
import HealthStats from "./HealthStats";
import ValueRow from "./ValueRow";

const MemberHealthDetailsModal = ({ kpi, onClose }) => {

  const [metric, setMetric] = useState(null);

  const columns = useMemo(() => [
    {
      id: "title",
      accessorKey: "title",
      header: () => <span>{I18n.t("insights.page.table.metric.title")}</span>,
      cell: info => info.getValue() ? (
        <span title={I18n.t(`insights.metrics.${info.getValue()}`)}>
          {I18n.t(`insights.metrics.${info.getValue()}`)}
        </span>
      ): null,
      enableSorting: true
    },
    {
      id: "category",
      accessorKey: "category",
      header: () => <span>{I18n.t("insights.page.table.category.title")}</span>,
      cell: info => info.getValue() ? I18n.t(`insights.page.table.category.${info.getValue()}`) : null,
      enableSorting: true
    },
    {
      id: "weight",
      accessorKey: "weight",
      header: () => <span>{I18n.t("insights.page.table.weight.title")}</span>,
      cell: info => info.getValue() ? parseFloat(info.getValue()) : null,
      enableSorting: true
    },
    {
      id: "scoring_scale",
      accessorKey: "scoring_scale",
      header: () => <span>{I18n.t("insights.page.table.scoring_scale.title")}</span>,
      cell: info => info.getValue() ? info.getValue() : null,
      enableSorting: true
    },
    {
      id: "subject",
      accessorKey: "subject",
      header: () => <span>{I18n.t("insights.page.table.subject.title")}</span>,
      cell: info => {
        const { category, subject, subject_id } = info.row.original;
        return category == "metadata"
          ? `${I18n.t(`insights.page.table.subject.${subject}`)}`
          : `${I18n.t(`insights.page.table.subject.${subject}`)} (#${subject_id})`
      },
      enableSorting: true
    },
    {
      id: "value",
      accessorKey: "value",
      header: () => <span>{I18n.t("insights.page.table.value.title")}</span>,
      cell: info => parseFloat(info.getValue()).toFixed(2),
      enableSorting: true
    },
    {
      id: "weighted_value",
      accessorKey: "weighted_value",
      header: () => <span>{I18n.t("insights.page.table.weighted_value.title")}</span>,
      cell: info => info.getValue() ? parseFloat(info.getValue()).toFixed(2) : null,
      enableSorting: true
    },
    {
      id: "final_value",
      accessorKey: "final_value",
      header: () => <span>{I18n.t("insights.page.table.final_value.title")}</span>,
      cell: info => info.getValue() ? parseFloat(info.getValue()).toFixed(2) : null,
      enableSorting: true
    }
  ], []);

  const initialState = {
    sorting: [
      {
        id: "title",
        desc: false
      }
    ]
  };

  const handleClose = () => {
    setMetric(null);
    onClose && onClose();
  }

  const selectMetric = (metric) => {
    if (metric?.category === "metadata" && metric?.flag) {
      setMetric(metric);
    }
  }

  const mapValueRowColumns = (values) => [
    "", "", "", "",
    parseFloat(values?.value).toFixed(3),
    parseFloat(values?.weighted_value).toFixed(3),
    parseFloat(values?.final_value).toFixed(3)
  ];

  const renderContent = () => kpi && (
    <div className="content">
      <div className="metrics-table">
        <Table
          columns={columns}
          data={kpi?.content_metrics?.metrics}
          initialState={initialState}
        />
        {kpi?.content_metrics?.final_value ? (
          <ValueRow
            className="members"
            title={I18n.t("insights.page.table.average.title")}
            columns={mapValueRowColumns(kpi?.content_metrics?.final_value)}
            background={kpi?.content_metrics?.final_value?.background}
          />
        ) : null}
        <Table
          columns={columns}
          data={kpi?.metadata_metrics?.metrics}
          initialState={initialState}
          onRowClick={selectMetric}
        />
        {kpi?.metadata_metrics?.final_value ? (
          <ValueRow
            className="members"
            title={I18n.t("insights.page.table.average.title")}
            columns={mapValueRowColumns(kpi?.metadata_metrics?.final_value)}
            background={kpi?.metadata_metrics?.final_value?.background}
          />
        ) : null}
        <KpiComposition
          steps={kpi?.steps}
          result={kpi?.current_value}
        />
      </div>
      <HealthStats
        flag={metric?.flag}
        flags={metric?.flags}
      />
    </div>
  );

  return (
    <InternalModal
      type="member-health-details"
      isModalOpen={Boolean(kpi)}
      hideModal={handleClose}
      showModalBody={false}
      component={renderContent()}
      backdrop
    />
  );
}

export default MemberHealthDetailsModal;
